import type { Resource } from '@getaccept/lib-shared/src/resources/types/resource';
import type { MaybeRef } from '@vueuse/core';
import { useAsyncState } from '@vueuse/core';
import { unref } from 'vue';
import { CreatePageType } from '@getaccept/lib-shared/src/types/create-page-type';
import type { CreatePageDTO } from '@getaccept/lib-shared/src/types/create-page-dto';
import { useToast } from '@getaccept/lib-shared/src/toast';
import type { Document } from '@getaccept/lib-shared/src/types/Document';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { CreateDocumentService } from '../services/create-document.service';
import type { CreateDocumentPatch } from '../types/create-document-types';
import { DocumentService } from '../../api/documents/services/document.service';
import { RecipientService } from '../../documents/services/recipient.service';

export const useCreateDocument = (documentId: MaybeRef<string>) => {
  const pageIndex = 0;

  const makeRecipientsViewers = async (recipients: Recipient[]) => {
    const recipientPayload = recipients.map(({ id }) => ({ id, role: RecipientRole.CC }));

    await Promise.all(
      recipientPayload.map((recipient: Recipient) =>
        RecipientService.update(unref(documentId), recipient).catch(error => {
          useToast().toast.danger(error);
        })
      )
    );
  };

  const addTemplateToDocument = async (
    template: Resource,
    isSigningDisabled: boolean
  ): Promise<CreateDocumentPatch> => {
    await CreateDocumentService.implementResource({
      documentId: unref(documentId),
      resourceId: template.id,
      contentId: template.contentId,
    });
    const data = await CreateDocumentService.implementTemplate(
      unref(documentId),
      template.contentId,
      pageIndex
    );

    // if template has isSigning on, but it has been toggled off when creating the document, and the template has recipients, make them viewers
    if (isSigningDisabled && data?.isSigning === 1 && !!data?.recipients?.length) {
      await makeRecipientsViewers(data.recipients);
    }
    return data;
  };

  const createInitialEditorBlock = async (): Promise<Document> => {
    const payload: CreatePageDTO = {
      documentId: unref(documentId),
      pageNum: pageIndex,
      pageType: CreatePageType.Editor,
    };
    return await DocumentService.createPage(payload);
  };

  const save = async (patch: CreateDocumentPatch, template?: Resource, importer?: boolean) => {
    try {
      let payload = patch;
      const isSigningDisabled = patch?.isSigning === 0;

      if (isSigningDisabled) {
        payload = { ...payload, isSigningOrder: 0, isSelfsign: 0 };
      }
      if (template) {
        await addTemplateToDocument(template, isSigningDisabled);
      }
      if (patch) {
        await CreateDocumentService.updateDocument(unref(documentId), payload);
      }
      if (!template && !importer) {
        return await createInitialEditorBlock();
      }
    } catch (error) {
      useToast().toast.danger(t('something-went-wrong'));
    }
  };

  return useAsyncState(save, null, { immediate: false });
};
